import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as ChevronDown } from '../../images/chevron-down.svg';
import { ReactComponent as RefreshIcon } from '../../images/refresh-icon.svg';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { ReactComponent as LineChartIcon } from '../../images/line-chart-up-01.svg';
import { ReactComponent as SettingsIcon } from '../../images/settings-02.svg';
import ChartOptions from '../common/ChartOptions';
import {
  fetchAssetTrendInitialStateAsync,
  setChartViewOptions,
  ChartViewOptions,
  setLiveDataEnabled,
  setEnableLiveToggle,
} from '../slices/AssetTrendDataSlice';
import { updateChartConfig, updateChartConfigUpdateStatus, updateViewType } from '../slices/ColumnSelectorSlice';
import { saveUserPerferences, DashboardConfig } from '../../dispatchers/UserPreferenceDispatcher';
import { ReactComponent as TaskIcon } from '../../images/file-check-02.svg';
import { ReactComponent as PencilIcon } from '../../images/pencil-line.svg';
import ColumnSelector from '../column-selector/colum-selector';
import { fetchDailyAverageInitialStateAsync } from '../slices/DailyAverageDataSlice';
import { setViewType } from '../slices/DailyAverageDataSlice';
import { hideTooltip, showTooltip } from '../common/simple-tool-tip/TooltipManager';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import EditYAxisModal from '../edit-y-axis/EditYAxisModal';
import { fetchPumpCheckerEvents } from '../../dispatchers/EventsDispatcher';
import useConfig from '../../hooks/useConfig';
import { ProtectAction } from '../../rbac';
import ToggleOption from '../common/ToggleOption';
import SuccessfullToast from '../../features/common/toast/SuccessfullToast';
import { isLiveStreamingEnabled } from '../../services/configuration/config';

interface TimeSeriesDataProps {
  timeseriesChartData: any;
  retrieveMinMax: boolean;
  assetId: any;
  openAddTaskModal: (status: true) => void;
  fetchCov: boolean;
  openAddNoteModal: (status: true) => void;
  selectedOption: string;
  isEditYAxisOpen?: boolean;
  setIsEditYAxisOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setYAxisRefreshVersion?: React.Dispatch<React.SetStateAction<number>>;
  rPermission: { [key: string]: boolean };
}
const ViewControls: React.FC<TimeSeriesDataProps> = ({
  timeseriesChartData,
  assetId,
  retrieveMinMax,
  openAddTaskModal,
  fetchCov,
  openAddNoteModal,
  selectedOption,
  isEditYAxisOpen,
  setIsEditYAxisOpen,
  setYAxisRefreshVersion,
  rPermission,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isChartViewOptionsOpen, setIsChartViewOptionsOpen] = useState(false);
  const [isChartConfigOpen, setIsChartConfigOpen] = useState(false);
  const [isConfigCheckBoxClicked, setsConfigCheckBoxClicked] = useState(false);
  const chartType = useAppSelector((state) => state.AssetAnalysis.chartType);
  const isTestDashboard = chartType?.toLowerCase().includes('tests');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const chartViewOptionsButtonRef = useRef<HTMLButtonElement>(null); // Ref for the button
  const chartViewOptionsRef = useRef<HTMLDivElement>(null);
  const chartViewOptions = isTestDashboard
    ? ['All', 'Split']
    : ['Stacked', '2-Y', 'Multi-Y', 'COV', 'Data', 'Daily average'];

  const dispatch = useAppDispatch();
  const { enablePumpChecker } = useConfig();

  const isRLChart = chartType === 'rl-well-charts';
  const isFacilityChart = chartType === 'facility-well-charts';

  const getColumnId = (item: any) => {
    if (isRLChart) {
      return item.address ?? item.name;
    }
    return item.name;
  };

  // Access startDate, endDate, and aggregate from Redux state
  const startDate = useAppSelector((state: any) => state.AssetTrendDataSlice.startDate);
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);
  const aggregate = useAppSelector((state: any) => state.AssetTrendDataSlice.aggregate);
  const isLiveDataEnabled = useAppSelector((state: any) => state.AssetTrendDataSlice.isLiveDataEnabled);
  const enableLiveToggle = useAppSelector((state: any) => state.AssetTrendDataSlice.enableLiveToggle);
  const liveDataTimeRange = useAppSelector((state: any) => state.AssetTrendDataSlice.liveDataTimeRange);
  const isAnyChartZoomed = useAppSelector((state: any) => state.AssetTrendDataSlice.isAnyChartZoomed);
  const userPreferenceData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const timeSeriesChart = isTestDashboard
    ? userPreferenceData?.find(
        (item: any) => item.key === (selectedOption === 'All' ? 'wellTestChart' : 'wellTestSplitChart'),
      )
    : userPreferenceData?.find((item: any) => item.key === 'timeseriesChart');
  const defaultViewOptions = timeSeriesChart?.widgetProperty?.widgetProperties?.viewOptions;
  const isCustomRange = useAppSelector((state: any) => state.AssetTrendDataSlice.isCustomRange);
  const parameterData = timeseriesChartData?.widgetProperty.widgetProperties.parameterConfig[0]?.parameters;
  const wellTestId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);
  const [localColumns, setLocalColumns] = useState(
    parameterData.map((item: any) => {
      const columnId = getColumnId(item);
      return {
        key: columnId,
        id: columnId,
        Header: item.name,
        isVisible: item.selected,
        LineColor: item.lineColor || '#00a86b',
        MarkerType: item.markerType || 'triangle',
        MarkerColor: item.markerColor || '#00a86b',
        MarkerSize: item.markerSize || 3,
        LineWeight: item.lineWeight || 1,
        LineType: item.lineType,
        DisplayOrder: item.displayOrder,
      };
    }) || [],
  );

  const [saveConfigResponse, setSaveConfigResponse] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [liveStreamingEnabled, setLiveStreamingEnabled] = useState(false);

  useEffect(() => {
    const chartViewOptions: ChartViewOptions = {
      chartType: defaultViewOptions.lineType,
      showTooltip: defaultViewOptions.showTooltips === 'true',
      showSD: defaultViewOptions.std === 'true',
      showEvents: defaultViewOptions.showEvents === 'true',
      isLogarithmic: defaultViewOptions.isLogarithmic === 'true',
    };
    dispatch(
      setChartViewOptions({
        chartViewOptions,
      }),
    );
  }, [defaultViewOptions]);

  // Handle async isLiveStreamingEnabled check
  useEffect(() => {
    const checkLiveStreamingEnabled = async () => {
      try {
        const enabled = await isLiveStreamingEnabled();
        setLiveStreamingEnabled(enabled);
      } catch (error) {
        console.error('Error checking live streaming status:');
        setLiveStreamingEnabled(false);
      }
    };
    checkLiveStreamingEnabled();
  }, []);

  // Handle live data settings based on selected option during initial load and option changes
  useEffect(() => {
    const isValidViewType = ['Stacked', '2-Y', 'Multi-Y'].includes(selectedOption);
    const isValidTimeRange = liveDataTimeRange == '24H';
    if (isValidViewType && isValidTimeRange) {
      dispatch(setEnableLiveToggle(true));
    } else {
      dispatch(setEnableLiveToggle(false));
      dispatch(setLiveDataEnabled(false));
    }
  }, [selectedOption, liveDataTimeRange, dispatch]);

  useEffect(() => {
    if (parameterData) {
      setLocalColumns(
        parameterData.map((item: any) => {
          const columnId = getColumnId(item);

          return {
            key: columnId,
            id: columnId,
            Header: item.name,
            isVisible: item.selected,
            LineColor: item.lineColor || '#00a86b',
            MarkerType: item.markerType || 'triangle',
            MarkerColor: item.markerColor || '#00a86b',
            MarkerSize: item.markerSize || 3,
            LineWeight: item.lineWeight || 1,
            LineType: item.lineType,
            DisplayOrder: item.displayOrder,
          };
        }) || [],
      );
    }
  }, [parameterData, chartType]);

  const handleReset = async () => {
    const fetchParams =
      (chartType === 'rl-well-charts' || chartType === 'facility-well-charts') && assetId
        ? { chartType, assetGUID: assetId }
        : chartType;
    const response = await dispatch(fetchColumnsData(fetchParams)).unwrap();

    const timeSeriesChart = response?.widgets.find(
      (item: any) =>
        item.key ===
        (isTestDashboard ? (selectedOption === 'All' ? 'wellTestChart' : 'wellTestSplitChart') : 'timeseriesChart'),
    );
    const parameterData = timeSeriesChart?.widgetProperty?.widgetProperties?.parameterConfig[0]?.parameters;

    setLocalColumns(
      parameterData.map((item: any) => {
        const columnId = getColumnId(item);

        return {
          key: columnId,
          id: columnId,
          Header: item.name,
          isVisible: item.selected,
          LineColor: item.lineColor || '#00a86b',
          MarkerType: item.markerType || 'triangle',
          MarkerColor: item.markerColor || '#00a86b',
          MarkerSize: item.markerSize || 3,
          LineWeight: item.lineWeight || 1,
          LineType: item.lineType,
          DisplayOrder: item.displayOrder,
        };
      }) || [],
    );
    dispatch(
      fetchAssetTrendInitialStateAsync({
        assetId,
        startDate,
        endDate,
        aggregate,
        aggregateMethod: 'last',
        retrieveMinMax,
        timeseriesChartData,
        fetchCov,
        chartType,
        ...(isRLChart ? { Addresses: getSelectedColumnsAsCSV(response?.widgets) } : {}),
      }),
    );
  };

  const getSelectedColumnsAsCSV = (columns: any) => {
    const timeSeriesWidget = isTestDashboard
      ? columns?.find((item: any) => item.key === (selectedOption === 'All' ? 'wellTestChart' : 'wellTestSplitChart'))
      : columns?.find((item: any) => item.key === 'timeseriesChart');

    if (!timeSeriesWidget) {
      return '';
    }

    const parameters = timeSeriesWidget?.widgetProperty?.widgetProperties?.parameterConfig?.[0]?.parameters || [];

    return parameters
      .filter((param: any) => param?.selected === true)
      .map((param: any) => param?.address)
      .filter(Boolean)
      .join(',');
  };

  const onApplyClicked = async (configUpdates: any, columns: any, saveConfig = false) => {
    let updatedParamConfig: any = [];
    const userPrefsDataCopy = [...userPreferenceData];
    const updatedWidget = userPrefsDataCopy.map((widget: any) => {
      const widgetCopy = { ...widget };

      if (widget?.key === 'timeseriesChart') {
        const widgetPropertyCopy = { ...widget.widgetProperty };
        const widgetPropertiesCopy = { ...widgetPropertyCopy.widgetProperties };

        // if (isConfigCheckBoxClicked) {
        //   widgetPropertyCopy.isDefault = false;
        // }
        const parameterConfigs = widgetPropertiesCopy.parameterConfig;
        const chartConfig = configUpdates;

        if (parameterConfigs) {
          widgetPropertiesCopy.parameterConfig = parameterConfigs.map((parameterConfig: any) => {
            const parameterConfigCopy = { ...parameterConfig };

            parameterConfigCopy.default = false;
            // if (parameterConfigCopy.name === 'User Graph View 1') {
            parameterConfigCopy.parameters = parameterConfig.parameters.map((config: any) => {
              const configCopy = { ...config };

              const configId = isRLChart ? (configCopy?.address ?? configCopy?.name) : configCopy?.name;

              const column = columns.find((item: any) => item.id === configId);

              if (column) {
                configCopy.displayOrder = column.DisplayOrder;
                configCopy.selected = column.isVisible ?? column.selected;
              }

              const currentConfig = chartConfig.find((chart: any) => chart.id === configId);
              if (currentConfig) {
                return {
                  ...configCopy,
                  lineColor: currentConfig.LineColor,
                  lineType: currentConfig.LineType,
                  lineWeight: currentConfig.LineWeight,
                  markerColor: currentConfig.MarkerColor,
                  markerType: currentConfig.MarkerType,
                  markerSize: currentConfig.MarkerSize,
                };
              }

              return configCopy;
            });
            // }

            return parameterConfigCopy;
          });

          updatedParamConfig = widgetPropertiesCopy.parameterConfig;
          // Set the updated properties
          widgetPropertyCopy.widgetProperties = widgetPropertiesCopy;
          widgetCopy.widgetProperty = widgetPropertyCopy;
        }
      }

      return widgetCopy;
    });

    if (isConfigCheckBoxClicked || saveConfig) {
      dispatch(updateChartConfigUpdateStatus({ isChartConfigUpdated: true }));
      const updatedParams = updatedParamConfig[0];
      // if (updatedParamConfig.length > 1) {
      //   updatedParams = updatedParamConfig.find((config: any) => config.name === 'User Graph View 1');
      // }
      const chartConfig: DashboardConfig = {
        DashboardName: chartType,
        WidgetName: isTestDashboard ? 'wellTestChart' : 'timeseriesChart',
        PropertyType: 'ParameterConfig',
        Preferences: {
          Id: '1',
          selected: true,
          Name: 'Default',
          Default: false,
          parameters: updatedParams.parameters,
        },
        ...((chartType === 'rl-well-charts' || chartType === 'facility-well-charts') && assetId
          ? { assetGUID: assetId }
          : {}),
      };
      await dispatch(saveUserPerferences(chartConfig));
      setSaveConfigResponse(true);
      setSuccessMessage('Successfully saved configuration.');
      setTimeout(() => {
        setSaveConfigResponse(false);
      }, 3000);
    }
    dispatch(updateChartConfig({ updatedWidget }));
    dispatch(
      fetchAssetTrendInitialStateAsync({
        assetId,
        startDate,
        endDate,
        aggregate,
        aggregateMethod: 'last',
        retrieveMinMax,
        timeseriesChartData,
        fetchCov,
        chartType,
        ...(!saveConfig && isRLChart ? { Addresses: getSelectedColumnsAsCSV(updatedWidget) } : {}), //  Apply only
      }),
    );
  };

  const saveConfigHasbeenClicked = (config: boolean) => {
    setsConfigCheckBoxClicked(config);
  };

  const onStandardDeviationChanged = () => {
    dispatch(
      fetchAssetTrendInitialStateAsync({
        assetId,
        startDate,
        endDate,
        aggregate,
        aggregateMethod: 'last',
        retrieveMinMax,
        timeseriesChartData,
        fetchCov,
        chartType,
      }),
    );
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target: any = event.target;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      // Check if the click is outside both the button and the ChartOptions container
      if (
        chartViewOptionsButtonRef.current &&
        !chartViewOptionsButtonRef.current.contains(target) &&
        chartViewOptionsRef.current &&
        !chartViewOptionsRef.current.contains(target as Node)
      ) {
        setIsChartViewOptionsOpen(false); // Close ChartOptions
      }
      // Check if the click is outside both the button and the ChartOptions container
      if (
        chartViewOptionsButtonRef.current &&
        !chartViewOptionsButtonRef.current.contains(target) &&
        chartViewOptionsRef.current &&
        !chartViewOptionsRef.current.contains(target as Node)
      ) {
        setIsChartViewOptionsOpen(false); // Close ChartOptions
      }
      const panelElement = document.querySelector('.main-container-panel');
      if (panelElement && !panelElement.contains(event.target as Node)) {
        setIsChartConfigOpen(false);
      }
      if (panelElement && !panelElement.contains(event.target as Node)) {
        setIsEditYAxisOpen?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const saveChartViewPreference = async (viewType: string) => {
    const chartConfig: DashboardConfig = {
      DashboardName: chartType,
      WidgetName: isTestDashboard ? 'wellTestChart' : 'timeseriesChart',
      PropertyType: 'ChartType',
      Preferences: viewType,
      ...((chartType === 'rl-well-charts' || chartType === 'facility-well-charts') && assetId
        ? { assetGUID: assetId }
        : {}),
    };
    await dispatch(saveUserPerferences(chartConfig));
  };

  const selectOption = (option: string) => {
    setIsDropdownOpen(false);
    dispatch(setViewType({ viewType: option }));
    saveChartViewPreference(option);
    if (option === 'Daily average') {
      loadDailyAverageData();
    }
    dispatch(updateViewType({ viewType: option }));
  };

  const loadDailyAverageData = async () => {
    if (startDate && endDate) {
      await dispatch(
        fetchDailyAverageInitialStateAsync({
          assetId,
          startDate: startDate,
          endDate: endDate,
          rodlift: isRLChart || isFacilityChart,
        }),
      );
    } else {
      console.error('Missing required parameters: startDate, endDate, or aggregate');
    }
  };

  const handleRefresh = async () => {
    if (selectedOption === 'Daily average') {
      loadDailyAverageData();
    }

    let startDateToUse = startDate;
    let endDateToUse = endDate;
    if (!isCustomRange) {
      // Only update time for predefined ranges
      const now = new Date();
      const setTimeToNow = (date: Date) => {
        if (!date) return date;
        const updated = new Date(date);
        updated.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
        return updated;
      };
      startDateToUse = setTimeToNow(startDate);
      endDateToUse = setTimeToNow(endDate);
    }

    if (startDateToUse && endDateToUse && aggregate) {
      await dispatch(
        fetchAssetTrendInitialStateAsync({
          assetId,
          startDate: startDateToUse,
          endDate: endDateToUse,
          aggregate,
          aggregateMethod: 'last',
          retrieveMinMax: retrieveMinMax,
          timeseriesChartData,
          fetchCov,
          chartType,
          isZoomedIn: isAnyChartZoomed,
          ...(isRLChart ? { Addresses: getSelectedColumnsAsCSV(userPreferenceData) } : {}),
        }),
      );
    } else {
      console.error('Missing required parameters: startDate, endDate, or aggregate');
    }
    const newEndDate = new Date(endDate);
    const formattedDate = new Date(newEndDate).toISOString();
    if (endDate && wellTestId) {
      dispatch(
        fetchPumpCheckerEvents({
          wellId: wellTestId,
          endDate: formattedDate,
        }),
      );
    }
  };

  const showChartViewOptions = () => {
    setIsChartViewOptionsOpen(!isChartViewOptionsOpen);
  };

  return (
    <>
      <SuccessfullToast
        isShowEnabled={saveConfigResponse}
        message={successMessage}
        callback={() => setSuccessMessage('')}
      />
      <div className='view-controls'>
        <div className='flex-display'>
          <div className='view-controls__dropdown' ref={dropdownRef}>
            <button
              analytics-id='view-controls-dropdown-btn'
              className='view-controls__dropdown-button'
              onClick={toggleDropdown}
            >
              <span className='view-controls__dropdown-text'>{selectedOption}</span>
              <div
                className={`view-controls__dropdown-icon ${isDropdownOpen ? 'view-controls__dropdown-icon--open' : ''}`}
              >
                <ChevronDown />
              </div>
            </button>

            {isDropdownOpen && (
              <div className='view-controls__dropdown-menu'>
                {chartViewOptions.map((option) => {
                  const isDisabled = chartType === 'rl-well-charts' && (option === '2-Y' || option === 'Multi-Y');
                  return (
                    <button
                      key={option}
                      analytics-id={`view-controls-dropdown-item-${option.replace(/\s+/g, '-').toLowerCase()}`}
                      className={`view-controls__dropdown-item ${
                        selectedOption === option ? 'view-controls__dropdown-item--active' : ''
                      } ${isDisabled ? 'view-controls__dropdown-item--disabled' : ''}`}
                      onClick={() => !isDisabled && selectOption(option)}
                      disabled={isDisabled}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className='view-controls__divider'></div>

          <div className='view-controls__buttons-container'>
            <div className='view-controls__button-group'>
              {chartType != 'rl-well-charts' && (
                <ProtectAction canAccess={rPermission.canAddTasks}>
                  <button
                    analytics-id='view-controls-add-task-btn'
                    className={`view-controls__icon-button ${!enablePumpChecker ? 'view-controls__icon-button--disabled' : ''}`}
                    onClick={() => openAddTaskModal(true)}
                    disabled={!enablePumpChecker}
                    onMouseEnter={(e) => showTooltip('Add tasks', e.currentTarget)}
                    onMouseLeave={hideTooltip}
                  >
                    <TaskIcon />
                  </button>
                </ProtectAction>
              )}
              {chartType != 'rl-well-charts' && (
                <ProtectAction canAccess={rPermission.canAddWellNotes}>
                  <button
                    analytics-id='view-controls-add-note-btn'
                    className={`view-controls__icon-button ${!enablePumpChecker ? 'view-controls__icon-button--disabled' : ''}`}
                    onClick={() => openAddNoteModal(true)}
                    disabled={!enablePumpChecker}
                    onMouseEnter={(e) => showTooltip('Well note', e.currentTarget)}
                    onMouseLeave={hideTooltip}
                  >
                    <PencilIcon />
                  </button>
                </ProtectAction>
              )}
              <ProtectAction canAccess={rPermission.canViewOptions}>
                <button
                  analytics-id='view-controls-settings-btn'
                  className='view-controls__icon-button'
                  onClick={showChartViewOptions}
                  ref={chartViewOptionsButtonRef}
                  onMouseEnter={(e) => showTooltip('Chart type', e.currentTarget)}
                  onMouseLeave={hideTooltip}
                >
                  <LineChartIcon />
                  <span
                    className={`view-controls__chart-icon-chevron ${
                      isChartViewOptionsOpen ? 'view-controls__chart-icon-chevron--open' : ''
                    }`}
                  >
                    <ChevronDown />
                  </span>
                </button>
              </ProtectAction>
              {!isTestDashboard && (
                <ProtectAction canAccess={rPermission.canConfigureCharts}>
                  <button
                    analytics-id='view-controls-settings-btn'
                    className='view-controls__icon-button'
                    onClick={() => setIsChartConfigOpen(true)}
                    onMouseEnter={(e) => showTooltip('Configure chart', e.currentTarget)}
                    onMouseLeave={hideTooltip}
                  >
                    <SettingsIcon />
                  </button>
                </ProtectAction>
              )}
            </div>
          </div>
          {!isTestDashboard && liveStreamingEnabled && (
            <div
              onMouseEnter={(e) => {
                if (!enableLiveToggle) {
                  showTooltip(
                    'Live data is only available for 24H ranges of Stacked, 2-Y, and Multi-Y views',
                    e.currentTarget,
                  );
                }
              }}
              onMouseLeave={hideTooltip}
              style={{
                opacity: enableLiveToggle ? 1 : 0.9,
                cursor: enableLiveToggle ? 'pointer' : 'not-allowed',
              }}
            >
              <ToggleOption
                label='Live data'
                enabled={isLiveDataEnabled}
                onChange={(enabled) => dispatch(setLiveDataEnabled(enabled))}
                className='live-data-compact'
                disabled={!enableLiveToggle}
              />
            </div>
          )}
          {!isTestDashboard && (
            <ProtectAction canAccess={rPermission.canRefreshData}>
              <button
                analytics-id='view-controls-refresh-btn'
                className='view-controls__refresh-button'
                onClick={handleRefresh}
                onMouseEnter={(e) => showTooltip('Refresh data', e.currentTarget)}
                onMouseLeave={hideTooltip}
              >
                <RefreshIcon />
              </button>
            </ProtectAction>
          )}
        </div>
        {isChartViewOptionsOpen && (
          <div className='view-options-container' ref={chartViewOptionsRef}>
            <ChartOptions
              retrieveMinMax={retrieveMinMax}
              onStandardDeviationChanged={onStandardDeviationChanged}
              fetchCov={fetchCov}
              dashboardName={chartType}
              layout={selectedOption}
              assetId={assetId}
            />
          </div>
        )}
      </div>
      {/* Column Selector Fly-out Panel */}
      {isChartConfigOpen && (
        <ColumnSelector
          columnsData={localColumns}
          onUpdateColumns={(updated) => {
            setLocalColumns(updated);
            console.log('Updated columns:', updated);
          }}
          onClose={() => setIsChartConfigOpen(false)}
          onApplyClickedInColumn={onApplyClicked}
          saveConfigHasbeenClicked={saveConfigHasbeenClicked}
          resetToDefaultSystemClicked={() => {
            handleReset();
          }}
          isChartConfig={true}
        />
      )}

      {isEditYAxisOpen && (
        <EditYAxisModal
          columnsData={localColumns}
          onClose={() => setIsEditYAxisOpen && setIsEditYAxisOpen(false)}
          onYAxisApply={() => setYAxisRefreshVersion && setYAxisRefreshVersion((v) => v + 1)}
        />
      )}
    </>
  );
};

export default ViewControls;
